/* Tabela dentro do cartão de vidro: cabeçalho em CAIXA ALTA sobre surface-sunken,
   linhas que entram a 30 ms e clique na linha para abrir a gaveta de detalhe. */
import React from 'react';
import { Box, Td as CTd, Th as CTh, Table, Tbody, Text, Thead, Tr } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { C, MONO } from './ui';

export function Tabela({
  cabecalho,
  children,
}: {
  cabecalho: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box overflowX="auto">
      <Table size="sm" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <Thead>
          <Tr>{cabecalho}</Tr>
        </Thead>
        <Tbody>{children}</Tbody>
      </Table>
    </Box>
  );
}

export function Th({
  children,
  num,
  ...rest
}: React.ComponentProps<typeof CTh> & { num?: boolean }) {
  return (
    <CTh
      bg={C.sunken}
      color={C.subtle}
      fontFamily={MONO}
      fontSize="10.5px"
      fontWeight={500}
      letterSpacing="1.2px"
      textTransform="uppercase"
      py={3}
      px={4}
      borderColor={C.line}
      textAlign={num ? 'right' : undefined}
      whiteSpace="nowrap"
      {...rest}
    >
      {children}
    </CTh>
  );
}

export function Td({
  children,
  num,
  ...rest
}: React.ComponentProps<typeof CTd> & { num?: boolean }) {
  return (
    <CTd
      py={3}
      px={4}
      fontSize="13px"
      color={C.ink}
      borderColor={C.lineSoft}
      textAlign={num ? 'right' : undefined}
      fontFamily={num ? MONO : undefined}
      verticalAlign="middle"
      {...rest}
    >
      {children}
    </CTd>
  );
}

const MotionTr = motion(Tr);

export function Linha({
  i,
  selecionada,
  onClick,
  children,
}: {
  i: number;
  selecionada?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <MotionTr
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      // Cascata curta: com 20 linhas a 30 ms a última aparecia 0,6 s depois, e a cada
      // troca de filtro ou página a lista demorava para ficar legível.
      transition={{ duration: 0.2, delay: Math.min(i, 8) * 0.02 }}
      onClick={onClick}
      // Linha clicável também abre pelo teclado (Tab + Enter/Espaço). O alvo precisa ser
      // a própria linha: Enter num botão de ação dentro dela não pode abrir a gaveta junto.
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (ev: React.KeyboardEvent) => {
              if (ev.target !== ev.currentTarget) return;
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      cursor={onClick ? 'pointer' : undefined}
      bg={selecionada ? C.brand50 : undefined}
      _hover={onClick ? { bg: C.brand50 } : undefined}
      _focusVisible={{ outline: `2px solid ${C.accent}`, outlineOffset: '-2px' }}
    >
      {children}
    </MotionTr>
  );
}

/** Segunda linha da célula, menor e apagada (CNPJ, IBGE, "substitui a nº 56"). */
export function SubLinha({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="12px" color={C.subtle} mt={0.5}>
      {children}
    </Text>
  );
}

export function Vazio({ colunas, children }: { colunas: number; children: React.ReactNode }) {
  return (
    <Tr>
      <CTd
        colSpan={colunas}
        textAlign="center"
        py={10}
        color={C.subtle}
        fontSize="13px"
        borderColor={C.lineSoft}
      >
        {children}
      </CTd>
    </Tr>
  );
}
